import React, { useMemo, useState } from "react";

import { range } from "lodash";

import { Select } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";

interface Props {
  field_key: string;
  has_error?: boolean;
  default_month_value?: string;
  default_year_value?: string;
  onInputChange: (
    value: string,
    event: React.ChangeEvent<HTMLSelectElement>
  ) => void;
  onInputBlur?: () => void;
  fireAnalyticsEvent?: () => void;
  isDisabled?: boolean;
}

const createCardExpiryYearOptions = () => {
  const year = new Date().getFullYear();
  return range(year, year + 11).map(year => ({
    value: year,
    label: year
  }));
};

const createCardExpiryMonthOptions = () =>
  range(1, 13).map(month => ({
    value: month,
    label: month < 10 ? "0" + month : month
  }));

export const ExpiryInputV2: React.FC<Props> = props => {
  const {
    field_key,
    has_error = false,
    onInputChange,
    default_month_value = "",
    default_year_value = "",
    fireAnalyticsEvent,
    isDisabled
  } = props;
  const { translator } = useAppContext();
  const [month, setMonth] = useState(default_month_value);
  const [year, setYear] = useState(default_year_value);
  const cardExpiryYearOptions = useMemo(createCardExpiryYearOptions, []);
  const cardExpiryMonthOptions = useMemo(createCardExpiryMonthOptions, []);

  const renderSelect = (
    type: "year" | "month",
    label: string,
    options: { value: number; label: string | number }[],
    onChange: React.ChangeEventHandler<HTMLSelectElement> | undefined
  ) => {
    const data_id = `${field_key}_${type}`;
    const inputProps = {
      "data-id": data_id,
      "data-has-error": has_error
    };

    return (
      <div className="col-span-2">
        <Select
          labelText={label}
          isInvalid={has_error}
          onChange={onChange}
          onBlur={_handleInputBlur}
          value={type === "year" ? year : month}
          autoComplete={`cc-exp-${type}`}
          name={type === "year" ? "expiry_year" : "expiry_month"}
          isDisabled={isDisabled}
          {...inputProps}
        >
          {options.map(option => (
            <Select.Option key={option.label} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const onChangeYearInput: React.ChangeEventHandler<
    HTMLSelectElement
  > = event => {
    const selected_year = event.target.value;
    setYear(selected_year);
    if (month) {
      onInputChange(`${selected_year}${month}`, event);
    }
    fireAnalyticsEvent && fireAnalyticsEvent();
  };

  const onChangeMonthInput: React.ChangeEventHandler<
    HTMLSelectElement
  > = event => {
    const selected_month: string = event.target.value;
    setMonth(selected_month);
    if (year) {
      onInputChange(`${year}${selected_month}`, event);
    }
    fireAnalyticsEvent && fireAnalyticsEvent();
  };

  const _handleInputBlur = () => {
    props.onInputBlur && props.onInputBlur();
  };

  return (
    <div className="grid grid-cols-4 gap-200">
      {renderSelect(
        "month",
        translator.t("!checkout.payment.expiry-date.month"),
        cardExpiryMonthOptions,
        onChangeMonthInput
      )}
      {renderSelect(
        "year",
        translator.t("!checkout.payment.expiry-date.year"),
        cardExpiryYearOptions,
        onChangeYearInput
      )}
    </div>
  );
};
